import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import { placePopover, type Rect, type Size } from '../utils/popoverPlacement';
import { EventPreview } from './EventPreview';

export type MeasureNode = (node: View | null, relativeTo: View | null) => Promise<Rect | null>;

export interface EventPreviewLayerApi {
  /** The anchor's rect relative to the layer, or null when it cannot be measured. */
  measureAnchor: (node: View | null) => Promise<Rect | null>;
  present: (key: string, event: CalendarEvent, anchor: Rect) => void;
  dismiss: (key: string) => void;
}

interface PresentedPreview {
  key: string;
  event: CalendarEvent;
  anchor: Rect;
}

const DISMISS_GRACE_MS = 250;
const POPOVER_GAP = tokens.spacing.xs;
const POPOVER_MARGIN = tokens.spacing.sm;

const EventPreviewLayerContext = createContext<EventPreviewLayerApi | null>(null);

export function useEventPreviewLayer(): EventPreviewLayerApi | null {
  return useContext(EventPreviewLayerContext);
}

function measureRelative(node: View | null, relativeTo: View | null): Promise<Rect | null> {
  return new Promise((resolve) => {
    if (!node || !relativeTo) {
      resolve(null);
      return;
    }
    node.measureLayout(
      relativeTo,
      (x, y, width, height) => resolve({ x, y, width, height }),
      () => resolve(null),
    );
  });
}

function layoutSize(event: LayoutChangeEvent): Size {
  const { width, height } = event.nativeEvent.layout;
  return { width, height };
}

interface EventPreviewLayerProps {
  children: ReactNode;
  /** Injectable for tests; defaults to the platform's measureLayout. */
  measureNode?: MeasureNode;
}

// One overlay per calendar screen, drawn as the LAST child of the layer so it
// paints above every grid cell and event block, outside any cell's
// overflow:hidden — the reason a hover preview from a month chip or a
// bottom-of-day block used to be cut off or painted under its neighbours.
// Hosts (EventPreviewHost.web) hand it the event plus the measured anchor;
// this layer owns placement only.
export function EventPreviewLayer({
  children,
  measureNode = measureRelative,
}: Readonly<EventPreviewLayerProps>) {
  const layerRef = useRef<View>(null);
  const [presented, setPresented] = useState<PresentedPreview | null>(null);
  const [bounds, setBounds] = useState<Size | null>(null);
  const [popoverSize, setPopoverSize] = useState<Size | null>(null);

  const measureAnchor = useCallback(
    (node: View | null) => measureNode(node, layerRef.current),
    [measureNode],
  );

  // Leaving the trigger must not close the preview instantly: the pointer
  // needs a moment to travel onto the popover, whose own controls ("Open
  // lead") must be pressable. Dismissal is therefore deferred, cancelled while
  // the pointer rests on the popover, and completed when it leaves.
  const pendingDismiss = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerOnPopover = useRef(false);

  const cancelPendingDismiss = useCallback(() => {
    if (pendingDismiss.current) {
      clearTimeout(pendingDismiss.current);
      pendingDismiss.current = null;
    }
  }, []);

  const removeNow = useCallback((key: string) => {
    setPresented((current) => (current?.key === key ? null : current));
  }, []);

  const present = useCallback(
    (key: string, event: CalendarEvent, anchor: Rect) => {
      cancelPendingDismiss();
      pointerOnPopover.current = false;
      setPopoverSize(null);
      setPresented({ key, event, anchor });
    },
    [cancelPendingDismiss],
  );

  const dismiss = useCallback(
    (key: string) => {
      cancelPendingDismiss();
      if (pointerOnPopover.current) return;
      pendingDismiss.current = setTimeout(() => {
        pendingDismiss.current = null;
        removeNow(key);
      }, DISMISS_GRACE_MS);
    },
    [cancelPendingDismiss, removeNow],
  );

  useEffect(() => cancelPendingDismiss, [cancelPendingDismiss]);

  const api = useMemo<EventPreviewLayerApi>(
    () => ({ measureAnchor, present, dismiss }),
    [measureAnchor, present, dismiss],
  );

  // Web-only mouse handlers; react-native's View type does not declare them,
  // so they are spread as a plain object exactly as Card.tsx does.
  const popoverHoverHandlers: Record<string, unknown> = {
    onMouseEnter: () => {
      pointerOnPopover.current = true;
      cancelPendingDismiss();
    },
    onMouseLeave: () => {
      pointerOnPopover.current = false;
      if (presented) removeNow(presented.key);
    },
  };

  const placed =
    presented && bounds && popoverSize
      ? placePopover(presented.anchor, popoverSize, bounds, POPOVER_GAP, POPOVER_MARGIN)
      : null;

  return (
    <EventPreviewLayerContext.Provider value={api}>
      <View
        ref={layerRef}
        testID="event_preview_layer"
        style={styles.layer}
        onLayout={(e) => setBounds(layoutSize(e))}
      >
        {children}
        <View testID="event_preview_layer_overlay" style={styles.overlay} pointerEvents="box-none">
          {presented && (
            <View
              key={`${presented.key}:${presented.event.id}`}
              testID="event_preview_popover"
              pointerEvents="auto"
              {...popoverHoverHandlers}
              onLayout={(e) => setPopoverSize(layoutSize(e))}
              style={[
                styles.popover,
                placed
                  ? { top: placed.top, left: placed.left, opacity: 1 }
                  : {
                      top: presented.anchor.y + presented.anchor.height + POPOVER_GAP,
                      left: presented.anchor.x,
                      opacity: 0,
                    },
              ]}
            >
              <EventPreview event={presented.event} />
            </View>
          )}
        </View>
      </View>
    </EventPreviewLayerContext.Provider>
  );
}

const styles = StyleSheet.create({
  layer: {
    flex: 1,
    position: 'relative',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 50,
  },
  popover: {
    position: 'absolute',
  },
});
